import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import type { WhatsAppStatus } from "@/types";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";

const stateLabel: Record<WhatsAppStatus["state"], string> = {
  disconnected: "Disconnesso",
  connecting: "Connessione in corso…",
  qr: "In attesa scansione QR",
  connected: "Connesso ✓",
  logged_out: "Logout — ri-pairing richiesto",
};

const stateColor: Record<WhatsAppStatus["state"], string> = {
  disconnected: "#555577",
  connecting: "#5B3E8A",
  qr: "#E8613C",
  connected: "#2A7A4B",
  logged_out: "#E8613C",
};

export default function WhatsAppSetup() {
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [qrPng, setQrPng] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    api.get<WhatsAppStatus>("/admin/whatsapp/status")
      .then(setStatus)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
    // Polling 2s — leggero, solo se la pagina è aperta.
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
  }, []);

  // Aggiorna il PNG del QR quando cambia il timestamp
  useEffect(() => {
    if (status?.state === "qr" && status.lastQrAt) {
      // Cache buster per forzare il refresh dell'immagine
      const t = new Date(status.lastQrAt).getTime();
      // Token JWT serve per autenticarsi — uso URL diretta con header sarebbe meglio,
      // ma per immagini nel browser DOM l'unico modo "facile" è fetch+blob.
      const jwt = localStorage.getItem("jwt");
      fetch(`${import.meta.env.VITE_API_URL}/admin/whatsapp/qr.png?t=${t}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      })
        .then((r) => r.ok ? r.blob() : null)
        .then((b) => setQrPng(b ? URL.createObjectURL(b) : null))
        .catch(() => setQrPng(null));
    } else {
      setQrPng(null);
    }
  }, [status?.state, status?.lastQrAt]);

  async function start() {
    setStarting(true); setError(null);
    try {
      await api.post("/admin/whatsapp/start");
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setStarting(false);
    }
  }

  async function disconnect() {
    if (!confirm("Disconnettere WhatsApp? Per riconnettere dovrai ri-scannerizzare il QR.")) return;
    await api.post("/admin/whatsapp/disconnect");
    load();
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Spinner size={32} /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-5">
      <header className="flex justify-between items-baseline">
        <h1 className="text-3xl font-serif text-primary">WhatsApp</h1>
        <Link to="/admin/dashboard" className="font-mono text-[17px] text-secondary hover:text-primary">
          ← dashboard
        </Link>
      </header>

      {status && (
        <Card className="space-y-3">
          <div className="flex items-center gap-3">
            <Badge color={stateColor[status.state]}>{stateLabel[status.state]}</Badge>
            {status.jid && (
              <span className="text-sm text-secondary font-mono">{status.jid.split("@")[0]}</span>
            )}
            {status.connectedAt && (
              <span className="text-xs text-muted font-mono ml-auto">
                dal {new Date(status.connectedAt).toLocaleString("it-IT")}
              </span>
            )}
          </div>

          {status.lastError && (
            <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-3 text-sm text-red-300">
              Ultimo errore: {status.lastError}
            </div>
          )}

          {error && <div className="text-red-300 text-sm">{error}</div>}

          <div className="flex gap-2">
            {(status.state === "disconnected" || status.state === "logged_out") && (
              <Button onClick={start} loading={starting}>
                Avvia WhatsApp (genera QR)
              </Button>
            )}
            {status.state === "connected" && (
              <Button variant="danger" onClick={disconnect}>Disconnetti</Button>
            )}
          </div>
        </Card>
      )}

      {status?.state === "qr" && qrPng && (
        <Card className="text-center space-y-3 border-accent">
          <h2 className="font-mono text-xs uppercase tracking-wider text-accent">
            Scannerizza con il tuo telefono
          </h2>
          <img src={qrPng} alt="QR Code WhatsApp" className="mx-auto bg-white p-3 rounded-lg" />
          <ol className="text-sm text-secondary text-left max-w-xs mx-auto space-y-1">
            <li>1. Apri WhatsApp sul telefono del mittente</li>
            <li>2. Impostazioni → Dispositivi collegati</li>
            <li>3. Tocca "Collega un dispositivo"</li>
            <li>4. Inquadra il QR qui sopra</li>
          </ol>
          <p className="text-xs text-muted font-mono">
            Il QR si rigenera automaticamente ogni ~20 secondi.
          </p>
        </Card>
      )}

      {status?.state === "connected" && (
        <Card>
          <h2 className="font-mono text-xs uppercase tracking-wider text-secondary mb-3">
            Pronto all'invio
          </h2>
          <p className="text-sm text-secondary">
            WhatsApp è connesso. Quando invii credenziali da un test, puoi selezionare
            il canale WhatsApp e i messaggi partiranno con throttle di 3 secondi tra l'uno
            e l'altro (anti-ban).
          </p>
          <p className="text-xs text-muted font-mono mt-3">
            ⚠️ Usa un numero <strong>dedicato</strong>, non il personale. WhatsApp può bannare
            numeri usati per invii massivi non standard.
          </p>
        </Card>
      )}
    </div>
  );
}
